const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Sidecar metadata key that stores expiresAt (ms) for each storage key.
const META_PREFIX = '__rnr_ttl_local_e::';


const now = () => Date.now();

const canUseLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const isWebView = () =>
  typeof window !== 'undefined' && window.ReactNativeWebView != null;

const metaKeyFor = (key) => `${META_PREFIX}${key}`;

const toExpiresAt = (ttlMs) =>
  now() + (typeof ttlMs === 'number' && ttlMs > 0 ? ttlMs : DEFAULT_TTL_MS);

export function setLocalItemTtl(key, value, ttlMs = DEFAULT_TTL_MS) {
  if (!key) return;
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(key, String(value ?? ''));

    if (isWebView()) {
      // WebView: keep persistent, no TTL metadata.
      window.localStorage.removeItem(metaKeyFor(key));
      return;
    }

    window.localStorage.setItem(metaKeyFor(key), String(toExpiresAt(ttlMs)));
  } catch {
    // ignore storage failures (private mode / quota / disabled)
  }
}

export function getLocalItemTtl(key, ttlMsForMigration = DEFAULT_TTL_MS) {
  if (!key) return null;
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return null;

    if (isWebView()) {
      return raw;
    }

    const mk = metaKeyFor(key);
    const meta = window.localStorage.getItem(mk);
    const expiresAt = meta != null ? parseInt(meta, 10) : NaN;

    if (Number.isFinite(expiresAt) && now() > expiresAt) {
      window.localStorage.removeItem(key);
      window.localStorage.removeItem(mk);
      return null;
    }

    // Enforce policy: if key has no TTL metadata yet, assign one on read.
    if (!Number.isFinite(expiresAt)) {
      window.localStorage.setItem(mk, String(toExpiresAt(ttlMsForMigration)));
    }

    return raw;
  } catch {
    return null;
  }
}

export function removeLocalItem(key) {
  if (!key) return;
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.removeItem(key);
    window.localStorage.removeItem(metaKeyFor(key));
  } catch {
    // ignore
  }
}

/**
 * Browser-only enforcement: removes expired keys and assigns TTL metadata to keys missing it.
 * Also migrates legacy wrapped payloads back to raw values.
 */
export function purgeExpiredLocalStorage(ttlMsForUntrackedKeys = DEFAULT_TTL_MS) {
  if (!canUseLocalStorage()) return;
  try {
    if (isWebView()) {
      // WebView: remove TTL metadata if any exists, but keep values.
      const keys = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const k = window.localStorage.key(i);
        if (k) keys.push(k);
      }
      keys.forEach((k) => {
        if (k.startsWith(META_PREFIX)) window.localStorage.removeItem(k);
      });
      return;
    }

    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k) keys.push(k);
    }

    const nowMs = now();

    keys.forEach((k) => {
      if (k.startsWith(META_PREFIX)) {
        const originalKey = k.slice(META_PREFIX.length);
        const meta = window.localStorage.getItem(k);
        const expiresAt = meta != null ? parseInt(meta, 10) : NaN;
        if (!Number.isFinite(expiresAt)) {
          window.localStorage.removeItem(k);
          return;
        }
        if (nowMs > expiresAt) {
          window.localStorage.removeItem(originalKey);
          window.localStorage.removeItem(k);
        }
        return;
      }

      // Ensure TTL metadata exists for all keys.
      const mk = metaKeyFor(k);
      const meta = window.localStorage.getItem(mk);
      const expiresAt = meta != null ? parseInt(meta, 10) : NaN;
      if (!Number.isFinite(expiresAt)) {
        window.localStorage.setItem(mk, String(toExpiresAt(ttlMsForUntrackedKeys)));
      }
    });
  } catch {
    // ignore
  }
}

